import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { useSession } from "providers/SessionProvider";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { RootPaths } from "routes/RootRouter";
import { AuthService, TokenRenew } from "services/authService";

////////////////////////////////////////////////////////////////////////////////

const ISO_8601 = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2,3})(.[0-9]+)?Z/;
const DATE_TEST = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2,3})/;
const JSON_TEST = /^\{.*\}$/;

////////////////////////////////////////////////////////////////////////////////

/**
 * Restaura um objeto obtido pela API.
 * @param v
 */
function restoreResponse(v: any) {
    Object.keys(v).forEach((k) => {
        try {
            if (v.hasOwnProperty(k)) {
                if (typeof v[k] == "object" && v[k] !== null) {
                    restoreResponse(v[k]);
                }
                else {
                    if (ISO_8601.test(v[k]) || DATE_TEST.test(v[k])) {
                        v[k] = new Date(Date.parse(v[k]));
                    }
                    else if (JSON_TEST.test(v[k])) {
                        v[k] = JSON.parse(v[k]);
                    }
                }
            }
        }
        catch {
            //v[k] = v[k];
        }
    });
}

/**
 * Intercepta os erros das chamadas HTTP e analisa o status, redirecionando para a página mais adequada.
 * @param param0
 */
export const AxiosInterceptorsHandler: React.FC<{ children?: React.ReactNode; instances: AxiosInstance[]; }> = ({ instances, children }) => {
    const navigate = useNavigate();
    const session = useSession();
    const tokenRenewInstance = new TokenRenew();
    //const [error, setError] = useState<ApiProblemDetails | null>(null!);

    const interceptors = useRef<number[]>([]);

    useEffect(() => {
        instances.forEach((instance) => {
            const interceptor = instance.interceptors.response.use(
                res => {
                    if (res && res.status === 200) {
                        restoreResponse(res.data);
                    }
                    if (res && res.status === 204) {
                        res.data = null;
                    }
                    return res;
                },
                error => {
                    if (axios.isCancel(error)) {
                        (error as any)["canceled"] = true;
                    }
                    else if (axios.isAxiosError(error)) {

                        const response = error.response as AxiosResponse;
                        //const serverErrorReport = error && error.config ? ` (Url: '${error.config.url}' - ${error.message})` : "";
                        const apiException = response && response.data ? (response.data as ApiProblemDetails) : null;
                        const authErrors = response && response.headers ? response.headers["www-authenticate"] as string : undefined;
                        const originalRequest = error.config!;

                        if (
                            response
                            && response.status === 401
                            && authErrors !== undefined
                            && (authErrors.indexOf("invalid_token") > -1 || authErrors.indexOf("Bearer") > -1 || authErrors.indexOf("ClaimPrincipal_NOT_VALID") > -1)) {

                            return AuthService.updateToken(tokenRenewInstance)
                                .then((token) => {
                                    if (token != null) {
                                        originalRequest.headers.setAuthorization(`${token.token_type} ${token.access_token}`);
                                        return instance.request(originalRequest);
                                    }
                                    else {
                                        console.info("Login Expired while refreshing token.");
                                        session.clearSession(() => navigate(RootPaths.LoginExpired));
                                    }
                                });
                        }

                        if (error.message == "Network Error" || response.status == null) {
                            error = {
                                message: 'Não foi possível acessar a rede de dados',
                                stack: error.stack,
                                name: 'Erro de Rede'
                            } as Error;
                        }
                        else {
                            if (response.status === 0 || response.status === 503) {
                                navigate(RootPaths.ServerNotAvailable);
                                return null;
                            }

                            if (apiException && apiException.title === "Unauthorized") {
                                navigate(RootPaths.AccessDenied);
                                return null;
                            }

                            if (response.status === 401
                                && authErrors !== undefined
                                && (authErrors.indexOf("invalid_token") > -1 || authErrors.indexOf("Bearer") > -1)) {
                                session.clearSession(() => navigate(RootPaths.LoginExpired));
                                return null;
                            }

                            if (apiException != null) {
                                error = apiException;
                            }
                        }
                    }

                    // É um objeto do tipo ApiProblemDetails?
                    //if (error.hasOwnProperty("title")) {
                    //    // Manipula o erro.
                    //    setError(error);
                    //}

                    // Informa o Erro ao Componente Chamador.
                    return Promise.reject(error);
                });
            interceptors.current.push(interceptor);
        });

        return () => {
            interceptors.current.forEach((interceptor) => axios.interceptors.response.eject(interceptor));
        };
    }, [session]);

    return <>{children}</>;
};
